import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { subscribeToPush } from '../components/subscribe';
import { AuthService } from '../services/auth-service';

import { router } from '../router';
const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;





@customElement('app-settings')
export class AppSettings extends LitElement {

  static styles = css`
   :host {
  display: block;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background: var(--background-color, #f0f0f5); /* Default light mode */
  color: var(--text-color, #333);
  padding: 20px;
  box-sizing: border-box;
}

header {
  background-color: var(--header-background, #0078d4); /* Default header color */
  color: white;
  padding: 20px;
  text-align: center;
  font-size: 1.75rem;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

h1 {
  color: var(--text-color, #333);
  font-size: 2rem;
  margin-bottom: 20px;
  text-align: center;
}

.settings-container {
  background: var(--card-background, white);
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
  max-width: 500px;
  margin: 0 auto;
}
hr {
  border: 1px solid #ccc; /* Customize the color */
  margin: 20px 0; /* Adds spacing around the line */
}

.team-select {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.team-select label {
  font-size: 1.1rem;
  font-weight: bold;
  color: var(--text-color, #333);
}

.team-select select {
  padding: 12px;
  font-size: 1rem;
  border-radius: 8px;
  border: 1px solid var(--border-color, #ccc);
  width: 100%;
  background-color: var(--select-background, #f9f9f9);
  transition: border-color 0.3s ease, background-color 0.3s ease;
}

.team-select select:focus {
  border-color: var(--focus-border-color, #0078d4);
  background-color: var(--focus-background, #f0f9ff);
}

.logout-btn {
  background-color: var(--logout-button-background, #e60000);
  color: white;
  border: none;
  padding: 15px;
  border-radius: 8px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;
  margin-top: 30px;
  width: 100%;
}

.logout-btn:hover {
  background-color: var(--logout-button-hover, #cc0000);
  transform: scale(1.05);
}

.logout-btn span {
  margin-right: 10px;
}

.team-description {
  font-size: 0.9rem;
  color: var(--description-color, #666);
  margin-top: 10px;
}

.notification-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
}

.notification-toggle input {
  width: 20px;
  height: 20px;
}

.disabled {
  background-color: #ddd; /* Gray out the button */
  cursor: not-allowed; /* Show a "not-allowed" cursor */
}


/* Dark Mode styles */
@media (prefers-color-scheme: dark) {
  :host {
    --background-color: #121212;
    --text-color: #f0f0f5;
    --card-background: #1e1e1e;
    --border-color: #444;
    --focus-border-color: #0078d4;
    --select-background: #333;
    --focus-background: #2c2c2c;
    --logout-button-background: #b22222;
    --logout-button-hover: #d32f2f;
    --header-background: #333;
    --description-color: #ccc;
  }
}

  `;


 @state() private selectedTeam: string | null = null;


async connectedCallback() {
  super.connectedCallback();

  // Detect system theme preference and set the theme accordingly
  const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
  this.setTheme(prefersDarkMode ? 'dark' : 'light');

  // Update the theme-color meta tag for browser address bar
  const themeColorMeta = document.getElementById('theme-color-meta') as HTMLMetaElement;
  if (themeColorMeta) {
    themeColorMeta.setAttribute('content', prefersDarkMode ? '#121212' : '#ffffff');
  }

  // Listen for changes in theme preference
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (event) => {
    this.setTheme(event.matches ? 'dark' : 'light');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', event.matches ? '#121212' : '#ffffff');
    }
  });

  // Check localStorage for saved team and notification preference
  const savedTeam = localStorage.getItem('selectedTeam');
  if (savedTeam) {
    this.selectedTeam = savedTeam;
  }
  // If no saved team in localStorage, fetch from the server
  if (!savedTeam) {
    try {
      const response = await fetch(`${apiServerUrl}/workers/${AuthService.workerNumber}/team`, {
        headers: { Authorization: `Bearer ${AuthService.getToken()}` },
      });
      if (!response.ok) throw new Error('Failed to fetch team');
      const data = await response.json();
      this.selectedTeam = data.team || 'T1'; // Default to 'T1' if no team is found
      localStorage.setItem('selectedTeam', this.selectedTeam ?? 'defaultTeam');
    } catch (error) {
      console.error('Error fetching team:', error);
    }
  }

  // Ensure a re-render after state updates
  this.requestUpdate();
}



updated() {
  const select = this.shadowRoot?.getElementById('team') as HTMLSelectElement;
  if (select) {
    select.value = this.selectedTeam || '';
  }
}


  private setTheme(theme: string) {
    document.body.setAttribute('theme', theme);
  }

  private async handleTeamChange(event: Event) {
  const select = event.target as HTMLSelectElement;
  const selectedTeam = select.value;

  try {
    // Update the team on the server
    const response = await fetch(`${apiServerUrl}/workers/${AuthService.workerNumber}/team`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${AuthService.getToken()}`,
      },
      body: JSON.stringify({ team: selectedTeam }),
    });

    if (!response.ok) throw new Error('Failed to update team');

    // Update the state and localStorage immediately after the team change
    this.selectedTeam = selectedTeam;
    localStorage.setItem('selectedTeam', selectedTeam); // Store the new team in localStorage

    // Trigger re-render to reflect the change
    this.requestUpdate();
    console.log('Team updated:', selectedTeam);
  } catch (error) {
    alert('Failed to update team. Please try again.');
  }
}

  private handleLogout() {
    AuthService.logout();
    router.navigate('');
    console.log('User logged out');
  }






render() {
  return html`
    <app-header ?title="My Monthly Calendar"></app-header>
    <h1>Settings</h1>
    <div class="settings-container">
      <div class="team-select">
        <label for="team">Select Team</label>
       <select id="team" @change=${this.handleTeamChange} .value=${this.selectedTeam || 'T1'}>
          <option value="T1">Team 1 (T1)</option>
          <option value="T2">Team 2 (T2)</option>
          <option value="T3">Team 3 (T3)</option>
          <option value="T4">Team 4 (T4)</option>
          <option value="T5">Team 5 (T5)</option>
        </select>
        <p class="team-description">
          Team to be used to generate your future calendar.
        </p>
      </div>
  <!-- Horizontal Bar -->
  <hr>
      <div class="notification-toggle">
        <div class="notification-toggle">
  <label for="notifications">Receive Schedule Update Notifications</label>
<button
  id="notification-button"
  @click=${subscribeToPush}
>
  Subscribe
</button>


</div>

      </div>

      <button class="logout-btn" @click=${this.handleLogout}>
        <span>🔑</span> Logout
      </button>
    </div>
  `;
}

}
